import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

interface Client {
  chatId: string;
  displayName: string;
  chatType: string;
}

interface ClientFilterProps {
  selectedClient: string | null;
  onClientChange: (clientId: string | null) => void;
  className?: string;
}

const ClientFilter: React.FC<ClientFilterProps> = ({
  selectedClient,
  onClientChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { theme } = useTheme();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/stats/clients');
      const data = response.data;
      if (data.success && data.data && Array.isArray(data.data)) {
        setClients(data.data);
      } else {
        setClients([]);
        console.warn('Invalid clients data format:', data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = (clients || []).filter(client =>
    client.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.chatId.includes(searchTerm)
  );

  const handleClientSelect = (clientId: string) => {
    onClientChange(clientId === selectedClient ? null : clientId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onClientChange(null);
    setSearchTerm('');
  };

  const selectedClientData = (clients || []).find(client => client.chatId === selectedClient);

  return (
    <div className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-3 text-sm border border-input bg-background text-foreground rounded-lg transition-colors hover:bg-accent hover:border-border"
      >
        <div className="flex items-center space-x-2">
          <span className="hidden sm:inline">Клиент:</span>
          <span className="font-medium">
            {selectedClientData ? selectedClientData.displayName : 'Все клиенты'}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          {selectedClient && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
              title="Очистить фильтр"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-lg shadow-lg border border-border bg-popover">
          <div className="p-4">
            {/* Search input */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Поиск клиентов..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input bg-background text-foreground rounded-md transition-colors focus:border-ring focus:ring-ring focus-visible:outline-none focus-visible:ring-2"
                autoFocus
              />
            </div>

            {/* Client list */}
            <div className="max-h-60 overflow-y-auto">
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Загрузка клиентов...
                  </p>
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground">
                    {searchTerm ? 'Клиенты не найдены' : 'Нет доступных клиентов'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredClients.map((client) => (
                    <button
                      key={client.chatId}
                      type="button"
                      onClick={() => handleClientSelect(client.chatId)}
                      className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                        client.chatId === selectedClient
                          ? 'bg-accent text-accent-foreground'
                          : 'text-popover-foreground/70 hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="truncate">{client.displayName}</span>
                        <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                          {client.chatType === 'private' ? 'Личный' : 'Группа'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Close button */}
            <div className="mt-4 pt-3 border-t border-border">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="w-full px-4 py-2 text-sm font-medium rounded-md transition-colors bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default ClientFilter;
